/**
 * What people did to this client's work.
 *
 * **This screen records nothing.** Every line it shows has been written to the
 * audit trail since the feature that performs it shipped — money corrections,
 * exclusions, additions, verifications, source views, snapshot imports, filing
 * confirmations, period transitions. What was missing was anywhere to read it,
 * which is a different problem from not having it (owner ask, Q8 2026-09-10).
 *
 * **The detail is shown, not summarised away.** A reviewer's own note is the
 * only answer to "why did this row leave the return", and a log that prints
 * `overlay.row_excluded` without it has kept the shape of the event and thrown
 * away its content. Reason codes make the question countable; the note answers
 * it on this row.
 *
 * Paging is keyset on `at`, because an audit trail grows at the head: an offset
 * page two shifts under the reader every time anything happens.
 */

import { Alert, Button, Card } from '@fiveox/ui'
import { History, RefreshCw } from 'lucide-react'
import { useCallback, useEffect, useState } from 'react'

import { CLIENT, useEvaluation } from '../lib/access'
import type { ActivityEvent } from '../lib/api'
import { clients } from '../lib/api'
import { useScopedClient } from '../lib/clientScope'

/**
 * What each action MEANS, in the practitioner's words rather than the event
 * name's. An unmapped action falls back to its raw name — visibly ugly and
 * therefore noticed, which is the right failure for a log that gains actions
 * whenever a feature does.
 */
const ACTION_LABELS: Record<string, string> = {
  'overlay.corrected': 'Field corrected',
  'overlay.money_corrected': 'Amounts corrected',
  'overlay.row_excluded': 'Row taken out of the return',
  'overlay.row_restored': 'Row put back',
  'overlay.row_added': 'Row added by hand',
  'overlay.suggestion_accepted': 'Suggestion accepted',
  'document.verified': 'Document verified',
  'document.source_viewed': 'Source document opened',
  'document.uploaded': 'Document uploaded',
  'document.reclassified': 'Document reclassified',
  'period.state_changed': 'Period state changed',
  'period.filing_confirmed': 'Filing confirmed by the portal receipt',
  'period.snapshot_imported': 'Filed month imported as a snapshot',
  'client.created': 'Client created',
  'client.updated': 'Client updated',
  'client.archived': 'Client archived',
}

/** The keys worth showing first — the rest follow, so nothing is hidden. */
const LEADING = ['note', 'reason_code', 'content_key', 'from', 'to', 'ack_no']

function detailPairs(detail: Record<string, unknown>): Array<[string, string]> {
  const render = (v: unknown): string =>
    typeof v === 'object' && v !== null ? JSON.stringify(v) : String(v)
  const keys = Object.keys(detail ?? {})
  const ordered = [
    ...LEADING.filter((k) => keys.includes(k)),
    ...keys.filter((k) => !LEADING.includes(k)).sort(),
  ]
  return ordered.map((k) => [k, render(detail[k])])
}

export function Activity() {
  const { client } = useScopedClient()
  const { can } = useEvaluation()
  const [events, setEvents] = useState<ActivityEvent[]>([])
  const [cursor, setCursor] = useState<string | null>(null)
  const [filter, setFilter] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const load = useCallback(
    async (before?: string) => {
      if (!client) return
      setBusy(true)
      setError(null)
      try {
        const { data: body } = await clients.activity(client.id, {
          limit: 100,
          ...(before ? { before } : {}),
          ...(filter ? { action: filter } : {}),
        })
        // Appending on a cursor, replacing on a fresh read: the same call does
        // both, and getting it backwards silently duplicates the page.
        setEvents((prev) => (before ? [...prev, ...body.events] : body.events))
        setCursor(body.next_before)
      } catch (e) {
        setError(e instanceof Error ? e.message : String(e))
      } finally {
        setBusy(false)
      }
    },
    [client, filter],
  )

  useEffect(() => {
    void load()
  }, [load])

  if (!can(CLIENT, 'can_view')) {
    return <div className="gst-empty">You cannot view this client.</div>
  }

  const actions = Array.from(new Set(events.map((e) => e.action))).sort()

  return (
    <div>
      <header className="mb-6">
        <h1 className="gst-h1">
          <History size={20} /> Activity
        </h1>
        <p className="gst-sub">
          Everything people did to this client&rsquo;s work, newest first &mdash; who,
          when, and in their own words
        </p>
      </header>

      {error && (
        <Alert tone="error" className="mb-4">
          {error}
        </Alert>
      )}

      <div className="mb-4 flex flex-wrap items-center gap-2">
        <select
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          className="gst-select"
          aria-label="Filter by action"
        >
          <option value="">Every action</option>
          {actions.map((a) => (
            <option key={a} value={a}>
              {ACTION_LABELS[a] ?? a}
            </option>
          ))}
        </select>
        <Button type="button" variant="outline" size="sm" onClick={() => void load()}>
          <RefreshCw size={13} /> Refresh
        </Button>
      </div>

      {events.length === 0 && !busy ? (
        <div className="gst-empty">
          Nothing recorded for this client yet. Uploads, corrections, removals and
          approvals all appear here as they happen.
        </div>
      ) : (
        <ol className="space-y-2">
          {events.map((e) => (
            <li key={e.id}>
              <Card className="p-3">
                <div className="flex flex-wrap items-baseline justify-between gap-2">
                  <span className="text-sm font-medium text-foreground">
                    {ACTION_LABELS[e.action] ?? e.action}
                  </span>
                  <span className="gst-meta">
                    {new Date(e.at).toLocaleString()}
                    {' · '}
                    {/*
                      * `actor_kind` and not a name: this service holds no user
                      * directory, and inventing one to render a label here
                      * would be the shadow copy constitution §1 forbids. The
                      * id is what the trail records and what a query joins on.
                      */}
                    {e.actor_kind === 'user' ? 'a person' : e.actor_kind}
                    {e.actor_user_id ? ` (${e.actor_user_id.slice(0, 8)})` : ''}
                  </span>
                </div>
                {Object.keys(e.detail ?? {}).length > 0 && (
                  <dl className="mt-2 grid gap-x-4 gap-y-1 sm:grid-cols-[max-content_1fr]">
                    {detailPairs(e.detail).map(([k, v]) => (
                      <div key={k} className="contents">
                        <dt className="gst-meta">{k}</dt>
                        <dd className="gst-mono break-all text-xs">{v}</dd>
                      </div>
                    ))}
                  </dl>
                )}
              </Card>
            </li>
          ))}
        </ol>
      )}

      {cursor && (
        <div className="mt-4">
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={busy}
            onClick={() => void load(cursor)}
          >
            {busy ? 'Loading…' : 'Older'}
          </Button>
        </div>
      )}
    </div>
  )
}
